import {UpdateAdminEmailRequest} from "../../../types/admin";
import React, {useEffect, useState} from "react";
import {getEmailInfoAPI, testEmailAPI, updateAdminEmailAPI, verifyAdminEmailAPI} from "../../../services/admin";
import styles from "./UpdateEmailPopUp.module.css";
import Button from "../../../components/common/button/Button";
import {useAlert} from "../../../components/common/error/AlertContext";

interface UpdateEmailPopUpProps {
    isOpen: boolean;
    onClose: () => void;
}

//이메일 수정 팝업
const UpdateEmailPopUp: React.FC<UpdateEmailPopUpProps> = ({isOpen, onClose}) => {
    const [form, setForm] = useState<UpdateAdminEmailRequest>({
        email: '',
        server: '',
        port: 0,
        password: ''
    });
    const [initialForm, setInitialForm] = useState<UpdateAdminEmailRequest>({
        email: '',
        server: '',
        port: 0,
        password: ''
    });
    const [loading, setLoading] = useState(true);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const {showAlert} = useAlert();

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newEmail = e.target.value;
        setForm(prev => ({...prev, email: newEmail}));
        checkFormChanged({...form, email: newEmail});
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        let updatedValue = value;

        // 포트 번호 검증 (1-65535 사이)
        if (name === 'port') {
            const portNumber = parseInt(value) || 0;
            if (portNumber < 0) updatedValue = '0';
            if (portNumber > 65535) updatedValue = '65535';
        }

        const updatedForm = {
            ...form,
            [name]: name === 'port' ? parseInt(updatedValue) || 0 : updatedValue
        };

        setForm(updatedForm);
        checkFormChanged(updatedForm);
    };

    const checkFormChanged = (updatedForm: UpdateAdminEmailRequest) => {
        //값 1개라도 변경 시 변경 상태로 설정
        const isChanged =
            updatedForm.email !== initialForm.email ||
            updatedForm.server !== initialForm.server ||
            updatedForm.port !== initialForm.port ||
            updatedForm.password.trim() !== initialForm.password.trim();

        setIsFormChanged(isChanged);

        if (isChanged) {
            //변경 상태 시 인증 필요
            setIsVerified(false);
        }
    };

    const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVerificationCode(e.target.value);
    };

    const handleSendVerification = async () => {
        // 이메일 형식 검증
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.email)) {
            showAlert('유효한 이메일 주소를 입력하세요.', "error");
            return;
        }

        // 서버 및 포트 검증
        if (!form.server.trim()) {
            showAlert('메일 서버를 입력하세요.', "error");
            return;
        }

        if (form.port <= 0) {
            showAlert('유효한 포트 번호를 입력하세요.', "error");
            return;
        }

        try {
            setSending(true);
            //인증 코드 발송 API
            await testEmailAPI(form);
            showAlert('인증코드가 발송되었습니다.', "info");
        } catch (error) {
            console.error('Error sending verification code:', error);
            showAlert('인증코드 발송에 실패했습니다. 이메일 설정을 확인하세요.', "error");
        } finally {
            setSending(false);
        }
    };

    const handleVerify = async () => {
        // 인증코드 유효성 검사
        if (!verificationCode.trim()) {
            showAlert('인증코드를 입력하세요.', "error");
            return;
        }

        try {
            setVerifying(true);

            //코드 인증 API
            const verifyResponse = await verifyAdminEmailAPI(form.email, verificationCode);
            console.log(verifyResponse);

            // 응답 형식에 따른 검증 로직
            let verified;
            if (typeof verifyResponse === 'object' && verifyResponse !== null) {
                // 객체인 경우 (이메일이 포함된 응답)
                verified = verifyResponse.email === form.email;
            } else {
                // 문자열인 경우 ('false'가 아닌지 확인)
                verified = verifyResponse !== 'false';
            }

            // 상태 업데이트
            setIsVerified(verified);

            // 로컬 변수 사용하여 알림 표시
            if (verified) {
                showAlert('인증이 완료되었습니다.', "info");
            } else {
                showAlert('인증코드가 일치하지 않습니다.', "error");
            }
        } catch (error) {
            console.error('Error verifying code:', error);
            showAlert('인증 과정에서 오류가 발생했습니다.', "error");
        } finally {
            setVerifying(false);
        }
    };

    const handleSubmit = async () => {
        //인증 성공 시 변경 가능
        if (!isVerified) {
            showAlert('이메일 인증이 필요합니다.', "error");
            return;
        }

        // 최종 유효성 검사
        if (!form.email.trim() || !form.server.trim() || form.port <= 0) {
            showAlert('모든 필드를 올바르게 입력하세요.', "error");
            return;
        }

        try {
            setSubmitting(true);
            //이메일 변경 API
            await updateAdminEmailAPI(form);
            showAlert('이메일 설정이 저장되었습니다.', "info");
            onClose();
        } catch (error) {
            console.error('Error update email request:', error);
            showAlert('이메일 설정 저장 중 오류가 발생했습니다.', "error");
        } finally {
            setSubmitting(false);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            //이메일 정보 조회 API
            const response = await getEmailInfoAPI();
            const initialData = {
                email: response.email || '',
                server: response.server || '',
                port: response.port || 0,
                password: ''
            };
            setForm(initialData);
            setInitialForm(initialData);
            setIsFormChanged(false);
            setIsVerified(false);
            setVerificationCode('');
        } catch (error) {
            console.log(error);
            showAlert('이메일 정보를 불러오는 데 실패했습니다.', "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    if (loading) {
        return <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.loading}>데이터를 불러오는 중...</div>
            </div>
        </div>;
    }

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <h2 className={styles.title}>이메일 설정</h2>
                <div className={styles.content}>
                    <div className={styles.inputGroup}>
                        <label>이메일</label>
                        <div className={styles.emailWrapper}>
                            {/*이메일 입력*/}
                            <input
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={handleEmailChange}
                                className={styles.input}
                                placeholder="example@domain.com"
                            />
                            {isFormChanged && !isVerified && (
                                // 인증코드 발송 버튼
                                <Button
                                    variant="secondary"
                                    onClick={handleSendVerification}
                                    className={styles.verificationButton}
                                    disabled={sending}
                                >
                                    {sending ? '발송 중...' : '인증코드 발송'}
                                </Button>
                            )}
                        </div>
                        {isFormChanged && !isVerified && (
                            <div className={styles.verificationSection}>
                                <div className={styles.inputGroup}>
                                    {/*인증코드 입력*/}
                                    <input
                                        type="text"
                                        value={verificationCode}
                                        onChange={handleVerificationCodeChange}
                                        placeholder="인증코드 입력"
                                        className={styles.input}
                                    />
                                    {/*인증 버튼*/}
                                    <Button
                                        variant="secondary"
                                        onClick={handleVerify}
                                        disabled={!verificationCode.trim() || verifying}
                                    >
                                        {verifying ? '인증 중...' : '인증'}
                                    </Button>
                                </div>
                            </div>
                        )}
                        {isVerified && (
                            <div className={styles.verifiedBadge}>
                                ✓ 인증됨
                            </div>
                        )}
                    </div>
                    <div className={styles.inputGroup}>
                        <label>서버</label>
                        {/*이메일 서버 입력창*/}
                        <input
                            type="text"
                            name="server"
                            value={form.server}
                            onChange={handleInputChange}
                            className={styles.input}
                            placeholder="smtp.example.com"
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>포트</label>
                        {/*이메일 서버 포트 입력창*/}
                        <input
                            type="number"
                            name="port"
                            value={form.port}
                            onChange={handleInputChange}
                            className={styles.input}
                            placeholder="465"
                            min="1"
                            max="65535"
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>비밀번호</label>
                        {/*비밀번호 입력창*/}
                        <input
                            type="password"
                            name="password"
                            value={form.password}
                            onChange={handleInputChange}
                            className={styles.input}
                            placeholder="이메일 계정 비밀번호"
                        />
                    </div>
                    {/*안내 메시지 추가*/}
                    <div className={styles.infoMessage}>
                        <p>변경된 설정을 저장하려면 이메일 인증이 필요합니다.</p>
                        <p>이메일 서버 설정은 SMTP 서버 정보여야 합니다.</p>
                    </div>
                </div>
                <div className={styles.buttonSection}>
                    {/*취소 버튼*/}
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        취소
                    </Button>
                    {/*저장 버튼*/}
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={!isFormChanged || !isVerified || submitting}
                    >
                        {submitting ? '저장 중...' : '저장'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default UpdateEmailPopUp;